import React from 'react'
import { IParlayOutcome, IShopOutcome, Operators } from '../pick6/store/types';
import { Alert, AlertTitle, alpha, Box, Button, Grid, Stack, useTheme } from '@mui/material';
import { Loading } from './Loading';
import { getParlayDeepLink } from './store/types';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import LockIcon from '@mui/icons-material/Lock';
import SocialShareButton from './SocialShareButton';
import HiddenComponent from './Hidden';
import BetRow from './BetRow';

interface OwnProps {
  selectedLegs: IParlayOutcome[];
  timeZone: string;
  isDisplayUnlocked: boolean;
  handleRemoveOutcome: (o: IParlayOutcome) => void;
  handleToggle: () => void;
  isDisabled: (o: IParlayOutcome) => boolean;
  onGenerate: () => void;
  operator: Operators
  hideUrl: boolean;
  loading: boolean;
  state: string;
}

type Props = OwnProps;

const OutcomesList = (props: Props) => {
  const theme = useTheme();
  const type = 'Remove';
  const {
    selectedLegs,
    isDisplayUnlocked,
    handleRemoveOutcome,
    handleToggle,
    onGenerate,
    operator,
    hideUrl,
    loading,
    state,
  } = props;
  const isPickEm = operator !== Operators.NONE;
  const handleClick = (o: IParlayOutcome | IShopOutcome) => {
    handleRemoveOutcome(o as IParlayOutcome);
  };

  enum ErrorCodes {
    NO_PARLAYS,
    INITIAL_PARLAY_ONE_BET,
    PARLAY_NEED_MORE_THAN_ONE_BET,
  }

  const [errorCode, setErrorCode] = React.useState<ErrorCodes | null>(null);

  const onRemoveAllLegs = () => {
    selectedLegs.forEach(x => handleRemoveOutcome(x));
  }

  React.useEffect(() => {
    if ((selectedLegs && selectedLegs.length === 0) || !selectedLegs) {
      setErrorCode(ErrorCodes.NO_PARLAYS);
    }
    if (selectedLegs && selectedLegs.length === 1) {
      setErrorCode(ErrorCodes.INITIAL_PARLAY_ONE_BET);
    }
  }, []);

  React.useEffect(() => {
    if (selectedLegs && selectedLegs.length < 2) {
      setErrorCode(ErrorCodes.PARLAY_NEED_MORE_THAN_ONE_BET);
    } else {
      setErrorCode(null);
      fetchMatchedParlays();
    }
  }, [selectedLegs]);

  const fetchMatchedParlays = async () => {
    await fetch(`/api/parlays/matched?state=${state}&hashcodelist=${selectedLegs.map(x => x.hashCodeBetSideWithLine).join(',')}`)
      .then((res) => res.json())
  };

  const getDataPoints = (leg: IParlayOutcome) => {
    return [
      {
        header: 'EV',
        data: (leg.expectedRoi && leg.expectedRoiPct ? (leg.expectedRoi * 100).toFixed(1) + '%' : '') || '-',
        highlight: parseInt(leg.expectedRoiPct || '0') * .05,
      },
      {
        header: 'xWin',
        data: leg.expectedWinPct || '-',
        highlight: parseInt(leg.expectedWinPct || '0') / 200,
      },
    ];
  }

  if (loading) {
    return (
      <Loading totalBoxes={5} />
    );
  }

  return (
    <>
      {errorCode === ErrorCodes.NO_PARLAYS && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box mt={3} mb={2} style={{ backgroundColor: '#fff' }}>
              <Alert variant="outlined" severity="info">
                <AlertTitle>No {isPickEm ? 'entry' : 'parlay'} Available</AlertTitle>
                We couldn't find any picks based on your selections. Please
                try again!
              </Alert>
            </Box>
          </Grid>
        </Grid>
      )}
      {errorCode === ErrorCodes.INITIAL_PARLAY_ONE_BET && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box mt={3} mb={2} style={{ backgroundColor: '#fff' }}>
              <Alert variant="outlined" severity="info">
                Hmm... We couldn't find any parlays based on your selection,
                please try again or see our Top Play below!
              </Alert>
            </Box>
          </Grid>
        </Grid>
      )}
      {errorCode === ErrorCodes.PARLAY_NEED_MORE_THAN_ONE_BET && (
        <Grid container spacing={0} mt={2}>
          <Grid item xs={12}>
            <Stack direction='row' alignItems='center' borderRadius='10px' padding='12px 15px' boxShadow='0px 4px 4px 0px rgba(0, 0, 0, 0.25)' sx={{ backgroundColor: '#1C1C1C' }}>
              <Stack direction='column' paddingRight="10px" >
                <Box fontSize={{ xs: '12px', md: '16px' }} color='white' fontWeight={700} paddingBottom='4px'>Generate a Recommended Parlay!</Box>
                <Box color='white' fontSize={{ xs: '10px', md: '12px' }} fontWeight={500}>To get started, use this button to generate a recommended parlay or use the table to the right to manually add your parlay legs.</Box>
              </Stack>
              <Stack>
                <Button variant="outlined" onClick={onGenerate} disabled={!isDisplayUnlocked} endIcon={!isDisplayUnlocked && <LockIcon />} sx={{ borderRadius: '6px !important', fontSize: { xs: '10px', md: '14px' } }}>Generate</Button>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      )}
      {selectedLegs && selectedLegs.length > 0 && (
        <Grid container className="bet-rows-container" sx={{ marginLeft: '0' }}>
          <Stack direction='row' justifyContent='space-between' width='100%' paddingTop="16px" alignItems='center'>
            <Box fontWeight={700} fontSize='18px' color='white'>
              Current Parlay
            </Box>
            <Button
              sx={{
                color: `${alpha(theme.palette.common.white, 0.7)} !important`,
                textTransform: 'capitalize !important',
                fontSize: '12px',
                fontWeight: 500,
              }}
              endIcon={<RemoveCircleIcon />}
              onClick={onRemoveAllLegs}
            >
              Remove All Legs
            </Button>
          </Stack>
          <Grid xs={12} className="wrapper">
            {selectedLegs?.map((leg, index) => (
              <div
                style={{
                  paddingTop: 4, paddingBottom: 12, display: 'flex', justifyContent: 'center', alignItems: 'center',
                }}
                key={leg?.hashCode}
              >
                <BetRow
                  contrastCardBackground={true}
                  parlayOutcome={leg}
                  isUserPremium={isDisplayUnlocked}
                  type={type}
                  onClick={handleClick}
                  dataPoints={getDataPoints(leg)}
                />
              </div>
            ))}
          </Grid>
          {selectedLegs && selectedLegs.length > 0 && (
            <Box sx={{
              marginTop: '12px',
              [theme.breakpoints.down('xs')]: {
                width: '100%',
                margin: '12px auto 0px auto',
                ' & a': {
                  width: '100%',
                },
              },
            }}>
              <Stack spacing={2} direction='row'>
              <Button
                variant="contained"
                size="large"
                endIcon={<OpenInNewIcon />}
                className="betnow"
                href={hideUrl ? '' : getParlayDeepLink(selectedLegs)}
                target="_blank"
                style={{ pointerEvents: hideUrl ? 'none' : 'auto', width: '100%', backgroundColor: '#0A0A0A' }}
              >
                Bet Now
              </Button>
              <SocialShareButton shareLink={`${window.location.href.split('?')[0]}?outcomes=${selectedLegs.map(x => x.hashCodeBetSideWithLine).join(',')}`}/>
              </Stack>
            </Box>
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        <HiddenComponent mdUp implementation="js">
          <Box mt={2} sx={{
            backgroundColor: '#101010',
            textAlign: 'center',
          }}>
            <Button
              variant="outlined"
              size="large"
              color="primary"
              sx={{ width: '100%' }}
              onClick={() => handleToggle()}
            >
              Add Leg
            </Button>
          </Box>
        </HiddenComponent>
      </Grid>
    </>
  )
}

export default OutcomesList